/**
 * Document Store — Zustand
 * State management for the firm-wide Documents centre.
 *
 * Mirrors `customerStore`: the service owns I/O, the store owns the in-memory
 * list plus filter/sort/pagination/selection. On any failure we reset loading
 * and record the error message.
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  Document,
  DocumentFilters,
  UploadDocumentInput,
} from '@/types/document.types'
import { getDocumentService } from '@/services/documents'

// ============================================================================
// STORE INTERFACE
// ============================================================================

type SortBy = 'name' | 'category' | 'status' | 'createdAt' | 'fileSize'
type SortOrder = 'asc' | 'desc'

interface DocumentState {
  // Data
  documents: Document[]
  selectedDocument: Document | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: DocumentFilters
  searchQuery: string
  sortBy: SortBy
  sortOrder: SortOrder

  // Pagination
  page: number
  pageSize: number

  // Selection for bulk actions
  selectedIds: string[]
}

interface DocumentActions {
  // Loading
  loadDocuments: () => Promise<void>

  // CRUD
  uploadDocument: (input: UploadDocumentInput) => Promise<Document | null>
  deleteDocument: (id: string, reason?: string) => Promise<void>
  verifyDocument: (id: string, isVerified: boolean, notes?: string) => Promise<void>

  // Selection
  setSelectedDocument: (id: string | null) => void
  toggleSelectDocument: (id: string) => void
  selectAllDocuments: (ids: string[]) => void
  clearSelection: () => void

  // Filters / sort / search
  setFilters: (filters: Partial<DocumentFilters>) => void
  clearFilters: () => void
  setSearchQuery: (query: string) => void
  setSortBy: (sortBy: SortBy) => void
  setSortOrder: (order: SortOrder) => void

  // Pagination
  setPage: (page: number) => void
  setPageSize: (size: number) => void

  // Derived
  getFilteredDocuments: () => Document[]
  getDocumentById: (id: string) => Document | undefined
  getPaginatedDocuments: () => {
    items: Document[]
    total: number
    page: number
    pageSize: number
    totalPages: number
  }

  // Bulk
  bulkDelete: (ids: string[]) => Promise<void>
  bulkVerify: (ids: string[]) => Promise<void>

  // Reset
  resetStore: () => void
}

type DocumentStore = DocumentState & DocumentActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: DocumentState = {
  documents: [],
  selectedDocument: null,
  isLoading: false,
  error: null,
  filters: {
    category: 'all',
    status: 'all',
    uploadedAfter: undefined,
    uploadedBefore: undefined,
  },
  searchQuery: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  pageSize: 20,
  selectedIds: [],
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useDocumentStore = create<DocumentStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ========================================
        // Loading
        // ========================================

        loadDocuments: async () => {
          set({ isLoading: true, error: null })
          try {
            const documents = await getDocumentService().list()
            set({ documents, isLoading: false })
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load documents',
            })
          }
        },

        // ========================================
        // CRUD
        // ========================================

        uploadDocument: async (input: UploadDocumentInput) => {
          set({ isLoading: true, error: null })
          try {
            const created = await getDocumentService().upload(input)
            const now = new Date().toISOString()
            const resolvedDoc: Document = {
              ...created,
              createdAt: created.createdAt || now,
              updatedAt: created.updatedAt || now,
              isVerified: created.isVerified ?? false,
              fileSizeMb:
                created.fileSizeMb ??
                (created.fileSize
                  ? Math.round((created.fileSize / (1024 * 1024)) * 100) / 100
                  : 0),
            }
            set((state) => ({
              documents: [resolvedDoc, ...state.documents.filter((d) => d.id !== resolvedDoc.id)],
              isLoading: false,
            }))
            return resolvedDoc
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to upload document'
            set({
              isLoading: false,
              error: message,
            })
            throw err
          }
        },

        deleteDocument: async (id: string, reason = 'User requested deletion') => {
          set({ isLoading: true, error: null })
          try {
            await getDocumentService().remove(id, reason)
            set((state) => ({
              documents: state.documents.filter((d) => d.id !== id),
              selectedDocument:
                state.selectedDocument?.id === id ? null : state.selectedDocument,
              selectedIds: state.selectedIds.filter((i) => i !== id),
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to delete document',
            })
          }
        },

        verifyDocument: async (id: string, isVerified: boolean, notes?: string) => {
          set({ isLoading: true, error: null })
          try {
            const patch = await getDocumentService().verify(id, isVerified, notes)
            set((state) => ({
              documents: state.documents.map((d) =>
                d.id === id ? { ...d, ...patch } : d
              ),
              selectedDocument:
                state.selectedDocument?.id === id
                  ? { ...state.selectedDocument, ...patch }
                  : state.selectedDocument,
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to verify document',
            })
          }
        },

        // ========================================
        // Selection
        // ========================================

        setSelectedDocument: (id: string | null) => {
          const doc = id ? get().documents.find((d) => d.id === id) : null
          set({ selectedDocument: doc || null })
        },

        toggleSelectDocument: (id: string) => {
          set((state) => ({
            selectedIds: state.selectedIds.includes(id)
              ? state.selectedIds.filter((i) => i !== id)
              : [...state.selectedIds, id],
          }))
        },

        selectAllDocuments: (ids: string[]) => {
          set({ selectedIds: ids })
        },

        clearSelection: () => {
          set({ selectedIds: [] })
        },

        // ========================================
        // Filters / sort / search
        // ========================================

        setFilters: (newFilters: Partial<DocumentFilters>) => {
          set((state) => ({
            filters: { ...state.filters, ...newFilters },
            page: 1,
          }))
        },

        clearFilters: () => {
          set({ filters: initialState.filters, searchQuery: '', page: 1 })
        },

        setSearchQuery: (query: string) => {
          set({ searchQuery: query, page: 1 })
        },

        setSortBy: (sortBy: SortBy) => {
          set({ sortBy })
        },

        setSortOrder: (order: SortOrder) => {
          set({ sortOrder: order })
        },

        // ========================================
        // Pagination
        // ========================================

        setPage: (page: number) => {
          set({ page })
        },

        setPageSize: (pageSize: number) => {
          set({ pageSize, page: 1 })
        },

        // ========================================
        // Derived
        // ========================================

        getFilteredDocuments: () => {
          const { documents, filters, searchQuery, sortBy, sortOrder } = get()

          let filtered = [...documents]

          // Search
          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim()
            filtered = filtered.filter(
              (d) =>
                d.name.toLowerCase().includes(query) ||
                (d.description?.toLowerCase().includes(query) ?? false) ||
                d.category.toLowerCase().includes(query)
            )
          }

          // Category
          if (filters.category && filters.category !== 'all') {
            filtered = filtered.filter((d) => d.category === filters.category)
          }

          // Status
          if (filters.status && filters.status !== 'all') {
            filtered = filtered.filter((d) => d.status === filters.status)
          }

          // Date range (createdAt)
          if (filters.uploadedAfter) {
            const after = new Date(filters.uploadedAfter).getTime()
            filtered = filtered.filter((d) => new Date(d.createdAt).getTime() >= after)
          }
          if (filters.uploadedBefore) {
            const before = new Date(filters.uploadedBefore).getTime()
            filtered = filtered.filter((d) => new Date(d.createdAt).getTime() <= before)
          }

          // Sort
          filtered.sort((a, b) => {
            let comparison = 0
            switch (sortBy) {
              case 'name':
                comparison = a.name.localeCompare(b.name)
                break
              case 'category':
                comparison = a.category.localeCompare(b.category)
                break
              case 'status':
                comparison = a.status.localeCompare(b.status)
                break
              case 'fileSize':
                comparison = a.fileSize - b.fileSize
                break
              case 'createdAt':
              default: {
                const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0
                const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0
                comparison = (isNaN(timeA) ? 0 : timeA) - (isNaN(timeB) ? 0 : timeB)
                break
              }
            }
            return sortOrder === 'asc' ? comparison : -comparison
          })

          return filtered
        },

        getDocumentById: (id: string) => {
          return get().documents.find((d) => d.id === id)
        },

        getPaginatedDocuments: () => {
          const { page, pageSize } = get()
          const filtered = get().getFilteredDocuments()
          const total = filtered.length
          const totalPages = Math.ceil(total / pageSize)
          const startIndex = (page - 1) * pageSize
          const items = filtered.slice(startIndex, startIndex + pageSize)
          return { items, total, page, pageSize, totalPages }
        },

        // ========================================
        // Bulk
        // ========================================

        bulkDelete: async (ids: string[]) => {
          set({ isLoading: true, error: null })
          try {
            await getDocumentService().bulkDelete(ids)
            set((state) => ({
              documents: state.documents.filter((d) => !ids.includes(d.id)),
              selectedIds: [],
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to delete documents',
            })
          }
        },

        bulkVerify: async (ids: string[]) => {
          set({ isLoading: true, error: null })
          try {
            await getDocumentService().bulkVerify(ids)
            set((state) => ({
              documents: state.documents.map((d) =>
                ids.includes(d.id)
                  ? {
                      ...d,
                      isVerified: true,
                      status: 'verified',
                      verifiedAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    }
                  : d
              ),
              selectedIds: [],
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to verify documents',
            })
          }
        },

        // ========================================
        // Reset
        // ========================================

        resetStore: () => {
          set(initialState)
        },
      }),
      {
        name: 'taxhive-document-store',
        partialize: (state) => ({
          // Only persist UI preferences
          pageSize: state.pageSize,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
      }
    ),
    { name: 'DocumentStore' }
  )
)

// ============================================================================
// SELECTORS
// ============================================================================

export const selectDocuments = (state: DocumentStore) => state.documents
export const selectIsLoading = (state: DocumentStore) => state.isLoading
export const selectSelectedIds = (state: DocumentStore) => state.selectedIds

// ============================================================================
// UTILITY HOOKS
// ============================================================================

export function useActiveDocumentFilterCount(): number {
  const filters = useDocumentStore((s) => s.filters)
  const searchQuery = useDocumentStore((s) => s.searchQuery)

  let count = 0
  if (filters.category && filters.category !== 'all') count++
  if (filters.status && filters.status !== 'all') count++
  if (filters.uploadedAfter) count++
  if (filters.uploadedBefore) count++
  if (searchQuery.trim()) count++
  return count
}
